import type { ApolloClient } from "@apollo/client";
import {
  DocumentsExtractionEventsDocument,
  ExtractionJobDocument,
  type DocumentsExtractionEventsSubscription,
} from "@/graphql/generated/operations";
import { ExtractionEventType, ExtractionJobStatus } from "@/graphql/generated/types";

type ExtractionEvent = DocumentsExtractionEventsSubscription["documentsExtractionEvents"];
type ExtractionResult = ExtractionEvent["result"];

export type ExtractionOutcome =
  | { kind: "completed"; message?: string | null; result?: ExtractionResult }
  | { kind: "failed"; message?: string | null; result?: ExtractionResult }
  // The job outlived the client wait window; it keeps running server-side and
  // its result is delivered as a durable notification (genesis #113).
  | { kind: "timeout" }
  // The caller stopped watching (e.g. the dialog was closed); the job keeps
  // running server-side and its result arrives as a notification.
  | { kind: "cancelled" };

const DEFAULT_POLL_INTERVAL_MS = 10_000;
// Matches genesis' document-extraction response timeout (900s) plus headroom.
// Coupling invariant (#228 C4): must stay ABOVE genesis' jobHardTimeout (840s,
// genesis#218), which drives a stuck job to a terminal FAILED event/poll status
// before this cap — so the dialog shows a real failure, not the "still running,
// you'll be notified" timeout branch. Keep this > jobHardTimeout if either moves.
const DEFAULT_POLL_MAX_MS = 960_000;

/**
 * Awaits a documents-extraction job: consumes the live events subscription for
 * progress and the final result, while a parallel status poll acts as the
 * safety net for a silently dropped socket or a pod that died before emitting a
 * terminal event (the poll can only recover the terminal *status* — the caller
 * reconstructs the added powers/skills from its own before/after snapshot).
 */
export function awaitDocumentsExtraction(
  client: ApolloClient,
  jobId: string,
  opts: {
    onProgress?: (event: ExtractionEvent) => void;
    pollIntervalMs?: number;
    pollMaxMs?: number;
    signal?: AbortSignal;
  } = {},
): Promise<ExtractionOutcome> {
  const pollIntervalMs = opts.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const pollMaxMs = opts.pollMaxMs ?? DEFAULT_POLL_MAX_MS;

  return new Promise<ExtractionOutcome>((resolve) => {
    if (opts.signal?.aborted) {
      resolve({ kind: "cancelled" });
      return;
    }
    // Holder so the subscription's (possibly synchronous) first emission and the
    // poll timer can be torn down from finish() regardless of assignment order.
    const state: {
      settled: boolean;
      sub?: { unsubscribe: () => void };
      pollTimer?: ReturnType<typeof setTimeout>;
      onAbort?: () => void;
    } = { settled: false };
    const startedAt = Date.now();

    const finish = (run: () => void) => {
      if (state.settled) return;
      state.settled = true;
      state.sub?.unsubscribe();
      if (state.pollTimer) clearTimeout(state.pollTimer);
      if (state.onAbort) opts.signal?.removeEventListener("abort", state.onAbort);
      run();
    };

    state.onAbort = () => finish(() => resolve({ kind: "cancelled" }));
    opts.signal?.addEventListener("abort", state.onAbort);

    state.sub = client
      .subscribe({ query: DocumentsExtractionEventsDocument, variables: { jobId } })
      .subscribe({
        next: ({ data }) => {
          const event = data?.documentsExtractionEvents;
          if (!event) return;
          if (event.type === ExtractionEventType.Completed) {
            finish(() => resolve({ kind: "completed", message: event.message, result: event.result }));
          } else if (event.type === ExtractionEventType.Failed) {
            finish(() => resolve({ kind: "failed", message: event.message, result: event.result }));
          } else {
            opts.onProgress?.(event);
          }
        },
        // A socket error just drops the live channel; the status poll below keeps
        // the outcome recoverable, so we don't reject here.
        error: () => {},
      });

    const poll = async () => {
      if (state.settled) return;
      try {
        const { data } = await client.query({
          query: ExtractionJobDocument,
          variables: { jobId },
          fetchPolicy: "network-only",
        });
        const status = data?.extractionJob?.status;
        if (status === ExtractionJobStatus.Completed) {
          finish(() => resolve({ kind: "completed" }));
          return;
        }
        if (status === ExtractionJobStatus.Failed) {
          finish(() => resolve({ kind: "failed" }));
          return;
        }
      } catch {
        // Transient query failure — keep polling until the deadline.
      }
      if (state.settled) return;
      if (Date.now() - startedAt >= pollMaxMs) {
        finish(() => resolve({ kind: "timeout" }));
        return;
      }
      state.pollTimer = setTimeout(() => void poll(), pollIntervalMs);
    };

    state.pollTimer = setTimeout(() => void poll(), pollIntervalMs);
  });
}
